import {Card, DataStatus, openDeleteDialog, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';

import {descend, prop, sort} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  CustomNoticeListItem,
  useDeleteCustomNoticeMutation,
  useGetCustomNoticesQuery,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError, Section} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useCanAddCustomNotice} from '../../hooks/useCanAddCustomNotice';
import {openCreateCustomNoticeDialog} from '../../utils/openCreateCustomNoticeDialog';
import {openEditCustomNoticeDialog} from '../../utils/openEditCustomNoticeDialog';
import {CustomNoticeCard} from '../CustomNoticeCard/CustomNoticeCard';

const sortNoticesNewestFirst = sort<CustomNoticeListItem>(descend(prop('createdAt')));

interface CustomNoticeListProps extends TestIdProps {
  resourceId: EntityResourceIds;
  recordId: string;
}

export function CustomNoticeList(props: CustomNoticeListProps) {
  const {
    data: notices,
    isLoading,
    isError,
  } = useGetCustomNoticesQuery({
    resourceId: props.resourceId,
    recordId: props.recordId,
  });

  const {canAddCustomNotice} = useCanAddCustomNotice({
    resourceId: props.resourceId,
    recordId: props.recordId,
  });

  const [deleteCustomNotice] = useDeleteCustomNoticeMutation();

  const handleCreate = () =>
    openCreateCustomNoticeDialog({
      resourceId: props.resourceId,
      recordId: props.recordId,
      'data-testid': props['data-testid'],
    });

  const handleEdit = (notice: CustomNoticeListItem) =>
    openEditCustomNoticeDialog({
      resourceId: props.resourceId,
      recordId: props.recordId,
      notice,
      'data-testid': props['data-testid'],
    });

  const handleDelete = (notice: CustomNoticeListItem) =>
    openDeleteDialog({
      text: i18n.t('general.customNotices.labels.deleteConfirmation'),
      'data-testid': suffixTestId('deleteCustomNotice', props),
      onConfirm: () =>
        deleteCustomNotice({
          resourceId: props.resourceId,
          recordId: props.recordId,
          customNoticeId: notice.id,
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
          })
          .catch(handleApiError),
    });

  return (
    <Section data-testid={suffixTestId('customNoticeList-section', props)}>
      <Card
        title={i18n.t('general.customNotices.labels.title')}
        actions={[
          {
            type: 'button',
            variant: 'link',
            leftIcon: 'content/add_circle',
            size: 'small',
            title: i18n.t('general.customNotices.actions.create'),
            isDisabled: !canAddCustomNotice,
            onClick: handleCreate,
            'data-testid': suffixTestId('customNoticeList-create', props),
          },
        ]}
        data-testid={suffixTestId('customNoticeList-card', props)}
      >
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          isEmpty={isNilOrEmpty(notices)}
          emptyMessage={i18n.t('general.customNotices.labels.emptyMessage')}
          minHeight={50}
          data-testid={suffixTestId('customNoticeList', props)}
        >
          <VStack spacing={3}>
            {sortNoticesNewestFirst(notices ?? []).map((notice, index) => (
              <CustomNoticeCard
                key={notice.id}
                notice={notice}
                onEdit={() => handleEdit(notice)}
                onDelete={() => handleDelete(notice)}
                data-testid={suffixTestId(`customNoticeList-[${index}]`, props)}
              />
            ))}
          </VStack>
        </DataStatus>
      </Card>
    </Section>
  );
}
